import React, { useState, useRef, useEffect } from 'react';
import { Icon, Spinner, SpinnerSize, mergeStyleSets } from '@fluentui/react';
import { Robot, Eraser, X, Gauge, ChartBar, Table, Info, Check, PaperPlaneRight } from '@phosphor-icons/react';
import type { BuilderChatMessage } from '../../types/dashboard.types';

const LOADING_STEPS = [
  { icon: 'Search', text: 'Analyzing your request...' },
  { icon: 'DatabaseView', text: 'Querying the database...' },
  { icon: 'BarChart4', text: 'Building charts...' },
  { icon: 'SpeedHigh', text: 'Creating KPI cards...' },
  { icon: 'Table', text: 'Preparing data tables...' },
  { icon: 'GridViewMedium', text: 'Arranging dashboard layout...' },
];

interface Props {
  messages: BuilderChatMessage[];
  onSend: (text: string) => void;
  loading: boolean;
  datasetNames: string[];
  suggestions: string[];
  onSuggestionClick: (text: string) => void;
  onClearChat?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const s = mergeStyleSets({
  root: {
    width: '35%',
    minWidth: 320,
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column' as const,
    borderLeft: '1px solid #E1E1E1',
    background: '#FFFFFF',
    flexShrink: 0,
  },
  collapsed: {
    position: 'fixed' as const,
    bottom: 24,
    right: 24,
    zIndex: 1100,
  },
  collapseBtn: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    background: 'linear-gradient(135deg, #2e873d, #55a655)',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(46,135,61,0.35)',
    transition: 'transform 0.15s ease',
    selectors: { ':hover': { transform: 'scale(1.08)' } },
  },
  collapseHeaderBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 6px',
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    color: '#747474',
    fontSize: 12,
    cursor: 'pointer',
    selectors: { ':hover': { background: '#F5F5F5' } },
  },
  header: {
    padding: '12px 16px',
    borderBottom: '1px solid #E1E1E1',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#232323',
  },
  headerBeta: {
    fontSize: 10,
    fontWeight: 600,
    color: '#2e873d',
    background: '#d9f4d7',
    padding: '2px 8px',
    borderRadius: 999,
  },
  headerSpacer: {
    flex: 1,
  },
  clearBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    color: '#747474',
    fontSize: 11,
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    selectors: {
      ':hover': { background: '#F5F5F5', color: '#D13438' },
    },
  },
  messages: {
    flex: 1,
    overflow: 'auto',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  msgUser: {
    alignSelf: 'flex-end',
    background: '#2e873d',
    color: '#FFFFFF',
    borderRadius: '12px 12px 2px 12px',
    padding: '8px 14px',
    fontSize: 13,
    maxWidth: '85%',
    lineHeight: '1.5',
    wordBreak: 'break-word' as const,
  },
  msgAssistant: {
    alignSelf: 'flex-start',
    background: '#F5F5F5',
    color: '#232323',
    borderRadius: '12px 12px 12px 2px',
    padding: '10px 14px',
    fontSize: 13,
    maxWidth: '85%',
    lineHeight: '1.5',
    wordBreak: 'break-word' as const,
  },
  // AI message: circular avatar + bubble with a brand-green prose rail
  aiRow: {
    display: 'flex',
    gap: 9,
    alignItems: 'flex-start',
    alignSelf: 'flex-start',
    maxWidth: '92%',
  },
  aiAvatar: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2e873d, #55a655)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  aiBubble: {
    background: '#F4F6F5',
    borderLeft: '3px solid #2e873d',
    borderRadius: '4px 12px 12px 4px',
    padding: '10px 14px',
    fontSize: 13,
    lineHeight: '1.55',
    color: '#232323',
    wordBreak: 'break-word' as const,
  },
  headerAvatar: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2e873d, #55a655)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  // Widget-added confirmation strip
  widgetAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 9,
    padding: '7px 11px',
    borderRadius: 8,
    background: '#eef7ef',
    border: '1px solid rgba(46,135,61,0.22)',
  },
  waTick: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#2e873d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  waKind: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.07em',
    color: '#1a5c26',
    background: '#d9f4d7',
    padding: '1px 6px',
    borderRadius: 3,
  },
  waTitle: {
    fontSize: 11.5,
    fontWeight: 600,
    color: '#1a5c26',
  },
  // Per-message refine suggestions: category label + chips
  chipsWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
    marginTop: 10,
    marginLeft: 35,
    alignSelf: 'flex-start',
  },
  chipsLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase' as const,
    color: '#9aa0a6',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
    marginTop: 8,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 12px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    background: '#FFFFFF',
    color: '#2e873d',
    border: '1px solid rgba(46,135,61,0.3)',
    cursor: 'pointer',
    transition: 'all 0.13s ease',
    selectors: {
      ':hover': {
        background: '#eef7ef',
        borderColor: '#2e873d',
      },
    },
  },
  emptyRoot: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
    padding: '24px 16px',
  },
  welcomeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  welcomeAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2e873d, #55a655)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  capabilityCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    background: '#F9F9F9',
    border: '1px solid #F0F0F0',
  },
  capabilityIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#747474',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  tipText: {
    fontSize: 11,
    color: '#747474',
    lineHeight: '1.5',
    background: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderRadius: 6,
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
  },
  thinkingDots: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    background: '#F5F5F5',
    borderRadius: '12px 12px 12px 2px',
    alignSelf: 'flex-start',
    fontSize: 13,
    color: '#747474',
  },
  inputArea: {
    borderTop: '1px solid #E1E1E1',
    padding: '10px 12px',
    flexShrink: 0,
  },
  datasetChips: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap' as const,
    marginBottom: 8,
  },
  dsChip: {
    fontSize: 10,
    color: '#747474',
    background: '#F5F5F5',
    padding: '2px 8px',
    borderRadius: 999,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  input: {
    flex: 1,
    border: '1px solid #E1E1E1',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.12s ease',
    selectors: {
      ':focus': { borderColor: '#2e873d' },
    },
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: 'none',
    background: '#2e873d',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 0.12s ease',
    selectors: {
      ':hover': { background: '#256d31' },
      ':disabled': { opacity: 0.5, cursor: 'default' },
    },
  },
});

export default function BuilderChat({ messages, onSend, loading, datasetNames, suggestions, onSuggestionClick, onClearChat, isCollapsed, onToggleCollapse }: Props) {
  const [text, setText] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Cycle through loading steps
  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      return;
    }
    const timer = setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [loading]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, loadingStep]);

  const handleSend = () => {
    const t = text.trim();
    if (!t || loading) return;
    onSend(t);
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isCollapsed) {
    return (
      <div className={s.collapsed}>
        <button className={s.collapseBtn} onClick={onToggleCollapse} title="Open Dashboard AI">
          <Robot size={22} weight="regular" />
        </button>
      </div>
    );
  }

  return (
    <div className={s.root}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerAvatar}>
          <Robot size={14} color="#FFFFFF" weight="regular" />
        </div>
        <span className={s.headerTitle}>Dashboard Builder AI</span>
        <span className={s.headerBeta}>Beta</span>
        <div className={s.headerSpacer} />
        {onClearChat && messages.length > 0 && (
          <button className={s.clearBtn} onClick={onClearChat} title="Clear chat history">
            <Eraser size={12} weight="regular" />
            Clear
          </button>
        )}
        {onToggleCollapse && (
          <button className={s.collapseHeaderBtn} onClick={onToggleCollapse} title="Collapse chat">
            <X size={10} weight="regular" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className={s.messages}>
        {messages.length === 0 && !loading && (
          <div className={s.emptyRoot}>
            {/* Welcome */}
            <div className={s.welcomeRow}>
              <div className={s.welcomeAvatar}>
                <Robot size={18} color="#FFFFFF" weight="regular" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#232323' }}>Hi! I'm your dashboard builder</div>
                <div style={{ fontSize: 12, color: '#747474', marginTop: 2 }}>Tell me what data you want to visualise and I'll create it.</div>
              </div>
            </div>

            {/* Capabilities */}
            <div>
              <div className={s.sectionLabel} style={{ marginBottom: 8 }}>What I can create</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#d9f4d7' }}>
                    <Gauge size={15} color="#2e873d" weight="regular" />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>KPI Cards</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Key metrics with trends and icons</div>
                  </div>
                </div>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#E8F0FE' }}>
                    <ChartBar size={15} color="#2563EB" weight="regular" />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>Charts</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Bar, line, area, pie and donut charts</div>
                  </div>
                </div>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#FEF3C7' }}>
                    <Table size={15} color="#B45309" weight="regular" />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>Data Tables</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Sortable tables with formatted values</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tip */}
            <div className={s.tipText}>
              <Info size={12} color="#B45309" weight="regular" style={{ marginTop: 1, flexShrink: 0 }} />
              <span>You can ask me to change colours, resize, switch chart types, or remove any widget.</span>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <React.Fragment key={msg.id}>
            {msg.role === 'user' ? (
              <div className={s.msgUser}>{msg.content}</div>
            ) : (
              <div className={s.aiRow}>
                <div className={s.aiAvatar}>
                  <Robot size={14} color="#FFFFFF" weight="regular" />
                </div>
                <div className={s.aiBubble}>
                  {msg.content}
                  {msg.widgetAction && (
                    <div className={s.widgetAction}>
                      <span className={s.waTick}>
                        <Check size={9} weight="bold" color="#FFFFFF" />
                      </span>
                      {msg.widgetAction.action === 'add_widget' ? (
                        <>
                          <span className={s.waKind}>{(msg.widgetAction.widget?.type || 'widget').toUpperCase()}</span>
                          <span className={s.waTitle}>{msg.widgetAction.widget?.title || 'Widget'}</span>
                        </>
                      ) : (
                        <span className={s.waTitle}>
                          {msg.widgetAction.action === 'remove_widget' ? 'Removed widget' : 'Updated widget'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Refine suggestions after assistant messages */}
            {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && (
              <div className={s.chipsWrap}>
                <div className={s.chipsLabel}>Refine</div>
                <div className={s.chipRow}>
                  {msg.suggestions.map((sug, i) => (
                    <button key={i} className={s.chip} onClick={() => onSuggestionClick(sug)}>
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </React.Fragment>
        ))}

        {loading && (
          <div className={s.thinkingDots}>
            <Spinner size={SpinnerSize.xSmall} />
            <Icon iconName={LOADING_STEPS[loadingStep].icon} styles={{ root: { fontSize: 13, color: '#2e873d' } }} />
            {LOADING_STEPS[loadingStep].text}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Suggestions for empty state */}
      {messages.length === 0 && suggestions.length > 0 && !loading && (
        <div style={{ padding: '0 16px 8px' }}>
          <div className={s.sectionLabel} style={{ marginBottom: 6 }}>Start with one of these</div>
          <div className={s.chipRow}>
            {suggestions.map((sug, i) => (
              <button key={i} className={s.chip} onClick={() => onSuggestionClick(sug)}>
                {sug}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className={s.inputArea}>
        {datasetNames.length > 0 && (
          <div className={s.datasetChips}>
            {datasetNames.map((ds) => (
              <span key={ds} className={s.dsChip}>{ds}</span>
            ))}
          </div>
        )}
        <div className={s.inputRow}>
          <input
            className={s.input}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want to see..."
            disabled={loading}
          />
          <button className={s.sendBtn} onClick={handleSend} disabled={loading || !text.trim()}>
            <PaperPlaneRight size={14} weight="regular" />
          </button>
        </div>
      </div>
    </div>
  );
}
